import React from 'react';
import {
    CareTeamMemberDetails,
    UpsertCareTeamMember,
} from '../../../primary-record-types/apiTypes/medicalProfileService';
import InsuranceFields, {
    useInsuranceContactDetailsFields,
    useInsuranceInitialValues,
} from './CareTeamFields/InsuranceFields';
import ProviderFields, {
    useProviderContactDetailsFields,
    useProviderInitialValues,
} from './CareTeamFields/ProviderFields';
import SupportFields, { useSupportContactDetailsFields, useSupportInitialValues } from './CareTeamFields/SupportFields';
import { insuranceFormValuesToUpsert, providerFormValuesToUpsert, supportFormValuesToUpsert } from './utils';
import { CareTeamMemberType, InsuranceFormValue, ProviderFormValue, SupportFormValue } from './types';
import { CareTeamMemberSource } from '../../../utils/careTeamUtils';

export type UseCareTeamFormHelpersArgs = {
    initialCareTeamMemberDetails: CareTeamMemberDetails | null;
    isDisabled?: boolean;
};

export type RenderFormFieldsArgs = {
    values: InsuranceFormValue | ProviderFormValue | SupportFormValue;
    isDisabled: boolean;
    careTeamMemberType: CareTeamMemberType;
    source: CareTeamMemberSource;
    careTeamMemberId: string | undefined;
};

export type UseCareTeamFormHelpersReturnValue = {
    getInitialValues: (
        careTeamMemberType: CareTeamMemberType,
    ) => InsuranceFormValue | ProviderFormValue | SupportFormValue;
    getSubmitValues: (
        careTeamMemberType: CareTeamMemberType,
        values: InsuranceFormValue | ProviderFormValue | SupportFormValue,
    ) => UpsertCareTeamMember;
    renderFields: (args: RenderFormFieldsArgs) => React.JSX.Element;
};

export const useCareTeamFormHelpers = ({
    initialCareTeamMemberDetails,
    isDisabled,
}: UseCareTeamFormHelpersArgs): UseCareTeamFormHelpersReturnValue => {
    const insuranceInitialValues = useInsuranceInitialValues(
        initialCareTeamMemberDetails?.careTeamMemberType === 'insurance' ? initialCareTeamMemberDetails : undefined,
    );
    const providerInitialValues = useProviderInitialValues(
        initialCareTeamMemberDetails?.careTeamMemberType === 'provider' ? initialCareTeamMemberDetails : undefined,
    );
    const supportInitialValues = useSupportInitialValues(
        initialCareTeamMemberDetails?.careTeamMemberType === 'support' ? initialCareTeamMemberDetails : undefined,
    );
    const insuranceContactDetailsFields = useInsuranceContactDetailsFields(isDisabled);
    const providerContactDetailsFields = useProviderContactDetailsFields(isDisabled);
    const supportContactDetailsFields = useSupportContactDetailsFields(isDisabled);

    const getInitialValues = (careTeamMemberType: CareTeamMemberType) => {
        if (careTeamMemberType === 'insurance') {
            return insuranceInitialValues;
        } else if (careTeamMemberType === 'provider') {
            return providerInitialValues;
        } else {
            return supportInitialValues;
        }
    };

    const getSubmitValues = (
        careTeamMemberType: CareTeamMemberType,
        values: InsuranceFormValue | ProviderFormValue | SupportFormValue,
    ) => {
        if (values.careTeamMemberType === 'insurance') {
            return insuranceFormValuesToUpsert(
                values as any,
                getInitialValues('insurance') as InsuranceFormValue,
                insuranceContactDetailsFields.handleUpdateAfterSubmit,
            );
        } else if (values.careTeamMemberType === 'provider') {
            return providerFormValuesToUpsert(
                values as any,
                getInitialValues('provider') as ProviderFormValue,
                providerContactDetailsFields.handleUpdateAfterSubmit,
            );
        } else {
            return supportFormValuesToUpsert(
                values as any,
                getInitialValues('support') as SupportFormValue,
                supportContactDetailsFields.handleUpdateAfterSubmit,
            );
        }
    };

    const renderFields = ({
        values,
        isDisabled,
        careTeamMemberType,
        source,
        careTeamMemberId,
    }: RenderFormFieldsArgs) => {
        if (careTeamMemberType === 'insurance') {
            return (
                <InsuranceFields
                    values={values as any}
                    careTeamMemberId={careTeamMemberId}
                    isDisabled={isDisabled}
                    source={source}
                />
            );
        } else if (careTeamMemberType === 'provider') {
            return (
                <ProviderFields
                    values={values as any}
                    careTeamMemberId={careTeamMemberId}
                    isDisabled={isDisabled}
                    source={source}
                />
            );
        } else {
            return (
                <SupportFields
                    values={values as any}
                    isDisabled={isDisabled}
                    source={source}
                    careTeamMemberId={careTeamMemberId}
                />
            );
        }
    };

    return {
        getInitialValues,
        getSubmitValues,
        renderFields,
    };
};
